import { useEffect, useRef, useCallback, useState, MutableRefObject } from "react"
import { useMap } from "@vis.gl/react-google-maps"

import {
  CarMotionState,
  createMotionState,
  pushFix,
  resolvePose,
} from "@/services/map/car-motion-engine"
import {
  CarSpriteState,
  CarSpriteTransition,
  createSpriteState,
  resolveFrame,
} from "@/services/map/car-sprite-engine"
import { CarTrailState, pushTrailPoint } from "@/services/map/car-trail"
import { prefersReducedMotion, subscribeReducedMotion } from "@/utils/reducedMotion"
import { calculateDistance } from "@/utils/routeHelpers"

import {
  CROSSFADE_MS,
  DOCK_SETTLE_MS,
  SpriteVariant,
  areCoordsValid,
  buildScanBeamLayer,
  buildScanGroundLayer,
  buildScanStaticSheen,
  buildSpriteContainer,
  burstConfetti,
  calculateHeading,
  ensureDockKeyframes,
  ensureScanKeyframes,
  fallbackIconForFrame,
  frameBackgroundPosition,
  preloadSpriteSheets,
  spriteSheetUri,
} from "./smooth-car-sprite"

interface Location {
  latitude: number
  longitude: number
  timestamp?: number
  heading?: number
  speed?: number
}

interface SmoothCarMarkerProps {
  location: Location
  onClick?: () => void
  /**
   * PLA-267 arrival docking: a monotonic counter; every increment plays a
   * one-shot settle flourish (scale 1 -> 1.06 -> 1, ~700ms, soft stop) on the
   * car marker. The approach machine bumps it at most once per leg and never
   * under prefers-reduced-motion; the marker additionally no-ops when the
   * user prefers reduced motion or on the regular-marker fallback (no
   * AdvancedMarkerElement DOM content to animate).
   */
  dockingSignal?: number
  /**
   * PLA-272 stale-GPS affordance: reports engine staleness transitions (90s
   * without a fix, ADR-0030 S6) so the parent can pause the route shimmer.
   * Fired only on flips -- never per tick -- plus a final `false` on unmount
   * if the marker disappears while stale.
   */
  onStaleChange?: (isStale: boolean) => void
  /**
   * PLA-272: whether the stale-GPS affordance (60% dim + onStaleChange) is
   * meaningful for this placement. Static at-hub markers (phases 4/5) pass
   * false -- GPS silence is EXPECTED there (no live leg is streaming), so
   * dimming would misread as a problem. Default true. Flipping to false
   * mid-staleness undims immediately and reports `false` upward.
   */
  staleAffordance?: boolean
  /**
   * PLA-273 comet trail: shared buffer the CarTrailOverlay renders. When set,
   * every DISPLAYED pose is pushed per tick -- the tail traces the spline path,
   * not raw fixes (ADR-0030 S8) -- except under prefers-reduced-motion, which
   * never buffers. The buffer's speed gate drains the tail while parked, and
   * equally while stale (the engine holds position at rest, so nothing moves).
   */
  trail?: MutableRefObject<CarTrailState>
  /**
   * PLA-278: which bundled car sheet this marker renders -- "hdp" (HDP-liveried
   * transporter, driver outbound) or "customer" (the customer's own car: return
   * leg, at-hub inspection, pickup placeholder). Defaults to "hdp" so existing
   * callers are unchanged. Use the shared `carSpriteVariant` helper to derive it
   * from order phase + leg. Changing it on a mounted marker swaps the sprite
   * sheet in place (no flash -- both variants are preloaded).
   */
  spriteVariant?: SpriteVariant
  /**
   * PLA-292/294 at-hub inspection scanner: when true an animated brand-red 3D
   * scan sweep runs on the marker (ground pulse below car + masked beam above).
   * No-ops under prefers-reduced-motion and on the regular-marker fallback.
   * Under reduced-motion a static masked sheen + contact shadow is shown instead.
   * Pass true on phases 4/5 only.
   */
  scanning?: boolean
  /**
   * PLA-292 completion confetti: a monotonic counter -- every increment plays a
   * one-shot confetti burst from the marker centre. 0/undefined is a no-op
   * (same pattern as dockingSignal). No-ops under prefers-reduced-motion and on
   * the regular-marker fallback.
   */
  celebrationSignal?: number
}

/** PLA-272: container opacity while the engine reports a stale GPS pose. */
const STALE_DIM_OPACITY = 0.6

// Sprite sheet plumbing (PLA-262 / PLA-265) lives in ./smooth-car-sprite.
// Display motion (segment timing, spline path, velocity profile, catch-up,
// staleness, reduced-motion snap) lives in the pure CarMotionEngine
// (PLA-271, ADR-0030) -- this component is the imperative shell that pushes
// fixes in and projects the per-frame pose onto the marker.
//
// The marker renders the bundled pseudo-3D HDP car sprite (64 heading-indexed
// frames, frame 0 = north, clockwise) instead of a remote PNG. Heading is NOT
// taken from the location payload -- the Pusher handler hardcodes heading/speed
// to 0 -- it is derived from consecutive displayed positions and fed through
// CarSpriteEngine, whose speed gate discards stationary GPS noise.

export const SmoothCarMarker = ({
  location,
  onClick,
  dockingSignal,
  onStaleChange,
  staleAffordance = true,
  trail,
  spriteVariant = "hdp",
  scanning = false,
  celebrationSignal,
}: SmoothCarMarkerProps) => {
  const map = useMap()
  const markerRef = useRef<google.maps.marker.AdvancedMarkerElement | google.maps.Marker | null>(
    null,
  )
  const animationRef = useRef<number | null>(null)
  const currentPosition = useRef({ lat: location.latitude, lng: location.longitude })
  const useRegularMarker = useRef(false)

  // Display motion engine state -- owned here, advanced via pure pushFix /
  // resolvePose (the engine never reads the clock; this component feeds
  // Date.now() into both so fixes and ticks share one time source).
  const motionStateRef = useRef<CarMotionState>(createMotionState())

  // PLA-272: mirrors the engine's last reported staleness so the dim and
  // onStaleChange fire on transitions only (the engine clock is the source).
  const isStaleRef = useRef(false)
  const onStaleChangeRef = useRef(onStaleChange)
  onStaleChangeRef.current = onStaleChange
  // Latest-value ref: the tick callback must see staleAffordance flips
  // without re-creating (same pattern as the trail ref below).
  const staleAffordanceRef = useRef(staleAffordance)
  staleAffordanceRef.current = staleAffordance

  // PLA-273: latest-value ref -- the parent wires the trail buffer only during
  // live phases, and the tick callback must see flips without re-creating.
  const trailRef = useRef(trail)
  trailRef.current = trail

  // Stable ref to createMarker so the update effect can call it for lazy creation.
  const createMarkerRef = useRef<(() => void) | null>(null)

  // Create marker content once. markerWrapper is the 1x1 anchor div handed to
  // AdvancedMarkerElement; markerContent is the 60x60 sprite container
  // absolutely centered on it (PLA-274 -- keeps the car on the GPS point at
  // every zoom level).
  const markerWrapper = useRef<HTMLDivElement | null>(null)
  const markerContent = useRef<HTMLDivElement | null>(null)

  // Sprite engine state -- owned here, advanced via the pure resolveFrame.
  const spriteStateRef = useRef<CarSpriteState>(createSpriteState(location.heading ?? 0))
  // Two stacked sprite layers for the opacity crossfade between frames.
  const spriteLayers = useRef<[HTMLDivElement, HTMLDivElement] | null>(null)
  const activeLayerIndex = useRef(0)
  const renderedFrame = useRef(spriteStateRef.current.lastFrameIndex)
  // Last interpolated position fed to the engine (heading/speed derivation).
  const lastSampleRef = useRef<{ lat: number; lng: number; timeMs: number } | null>(null)

  // PLA-278: latest-value ref so the imperative marker-creation path reads the
  // current variant without re-creating the marker, and the swap effect below
  // can diff against the rendered variant.
  const spriteVariantRef = useRef<SpriteVariant>(spriteVariant)

  // PLA-294: two refs for the 3D scanner layers (ground below + beam above).
  // null when no layers are mounted.
  const scanGroundRef = useRef<HTMLDivElement | null>(null)
  const scanBeamRef = useRef<HTMLDivElement | null>(null)

  // PLA-292: marker DOM is created imperatively (and lazily -- first valid fix
  // may arrive after mount). The scanner/confetti effects must NOT run before
  // the wrapper/content exist, or they silently no-op forever. Flip this true
  // once createMarker() has built the marker so those effects re-run and apply.
  const [markerReady, setMarkerReady] = useState(false)
  // PLA-292: last celebrationSignal we actually burst for -- dedupes the two
  // triggers (signal change vs marker-ready) so the confetti fires exactly once.
  const lastBurstSignalRef = useRef<number | undefined>(undefined)
  // PLA-292: live reduced-motion preference. The scanner is a LOOP, so toggling
  // reduced motion mid-scan must stop it -- gate the scanner effect on this state
  // (a point-in-time prefersReducedMotion() read would never re-run on a toggle).
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion)
  useEffect(() => subscribeReducedMotion(setReducedMotion), [])

  // Warm BOTH variants' sheets (each at both densities) before the marker-
  // creation effect runs -- swapping legs (hdp <-> customer) never flashes empty
  // (PLA-278). preloadSpriteSheets is idempotent per variant.
  useEffect(() => {
    preloadSpriteSheets("hdp")
    preloadSpriteSheets("customer")
  }, [])

  // PLA-278: swap the sprite sheet in place when the variant changes on a
  // MOUNTED marker (e.g. the live driver flips from outbound HDP to the return
  // leg's customer car without remounting). Both layers point at the same
  // background-image, so repaint both to the new sheet at the current frame;
  // the regular-marker fallback re-sets its icon. No flash -- both variants are
  // preloaded above. Skips the initial render (ref already equals the prop).
  useEffect(() => {
    if (spriteVariantRef.current === spriteVariant) return
    spriteVariantRef.current = spriteVariant

    if (useRegularMarker.current) {
      if (markerRef.current && "setIcon" in markerRef.current) {
        ;(markerRef.current as google.maps.Marker).setIcon(
          fallbackIconForFrame(renderedFrame.current, spriteVariant),
        )
      }
      return
    }

    const layers = spriteLayers.current
    if (!layers) return
    const newSheet = `url(${spriteSheetUri(spriteVariant)})`
    for (const layer of layers) {
      layer.style.backgroundImage = newSheet
    }
  }, [spriteVariant])

  // PLA-267 docking settle: each dockingSignal increment plays one soft
  // scale 1 -> 1.06 -> 1 settle on the marker content. Signal 0 / undefined
  // (mount, no docking yet) and reduced motion are no-ops; the upstream
  // approach machine already suppresses the signal under reduced motion --
  // this guard covers a live preference flip between bump and paint.
  useEffect(() => {
    if (!dockingSignal) return undefined
    if (prefersReducedMotion()) return undefined
    const content = markerContent.current
    if (!content) return undefined // regular-marker fallback or not created yet

    ensureDockKeyframes()
    // Restart cleanly if a previous settle is somehow still mid-flight.
    content.style.animation = "none"
    void content.offsetWidth
    content.style.animation = `hdpCarDockSettle ${DOCK_SETTLE_MS}ms cubic-bezier(0.33, 1, 0.68, 1) 1`
    const clear = () => {
      content.style.animation = ""
    }
    content.addEventListener("animationend", clear, { once: true })
    return () => content.removeEventListener("animationend", clear)
  }, [dockingSignal])

  // PLA-294 at-hub scanner: mount/unmount the 3D scan layers on the 60x60
  // sprite container (markerContent) when `scanning` flips.
  // Ground layer inserted before sprite layers (sits below car).
  // Beam layer appended last (sits above car, masked to car silhouette).
  // Under reduced-motion: static contact shadow + static masked sheen only.
  // Never runs on the regular-marker fallback (no DOM content).
  useEffect(() => {
    const removeLayers = () => {
      if (scanGroundRef.current) {
        scanGroundRef.current.remove()
        scanGroundRef.current = null
      }
      if (scanBeamRef.current) {
        scanBeamRef.current.remove()
        scanBeamRef.current = null
      }
    }

    const content = markerContent.current
    // Tear down when not scanning, marker DOM not ready, or regular-marker fallback.
    if (!content || useRegularMarker.current || !scanning) {
      removeLayers()
      return removeLayers
    }

    // Always remove any existing layers before remounting -- ensures a clean slate
    // when reducedMotion toggles while scanning is active (otherwise the old
    // animated/static layers persist alongside the new ones).
    removeLayers()

    ensureScanKeyframes()

    // Mask to the CURRENTLY displayed sprite frame, not a hardcoded frame 0:
    // the car holds its last heading when parked at the hub (resolveFrame never
    // snaps back to north), so the at-hub frame is usually non-zero. applyFrame
    // keeps the mask in sync if the frame changes while the scanner is mounted.
    const maskUri = spriteSheetUri(spriteVariantRef.current)
    const maskPosition = frameBackgroundPosition(renderedFrame.current)

    // Ground layer (soft contact shadow, no animation) is identical in both
    // modes; only the beam differs: a masked sweep when animated, a static
    // masked sheen under reduced motion.
    const groundLayer = buildScanGroundLayer()
    content.insertBefore(groundLayer, content.firstChild)
    scanGroundRef.current = groundLayer

    if (reducedMotion) {
      const staticBeam = buildScanStaticSheen(maskUri, maskPosition)
      content.appendChild(staticBeam)
      scanBeamRef.current = staticBeam
    } else {
      const beamLayer = buildScanBeamLayer(maskUri, maskPosition)
      content.appendChild(beamLayer)
      scanBeamRef.current = beamLayer
    }

    return removeLayers
    // markerReady: marker DOM is built lazily, so re-run once it exists.
    // reducedMotion: live toggle must start/stop the animated layers.
  }, [scanning, markerReady, reducedMotion])

  // PLA-292 completion confetti: each non-zero celebrationSignal increment
  // fires one burst from the 1x1 wrapper (overflow visible -- particles escape
  // the 60x60 clip box and radiate from the car centre). No-ops under reduced
  // motion and on the regular-marker fallback.
  useEffect(() => {
    if (!celebrationSignal) return
    // Dedupe: this effect also re-runs when markerReady flips, so guard on the
    // signal value to burst exactly once per completion.
    if (lastBurstSignalRef.current === celebrationSignal) return
    if (prefersReducedMotion()) return
    if (useRegularMarker.current) return
    const wrapper = markerWrapper.current
    // Marker not built yet -- markerReady will re-trigger this effect once it is,
    // so the burst fires on the freshly-mounted completion marker.
    if (!wrapper) return
    lastBurstSignalRef.current = celebrationSignal
    burstConfetti(wrapper)
  }, [celebrationSignal, markerReady])

  /**
   * PLA-272 stale-GPS affordance: dims the marker to 60% while the engine
   * reports staleness; restores instantly on the next fix (the container pins
   * `transition: none`). Opacity-only on the container, composing with the
   * child sprite-layer crossfade and the transform-based dock settle. Stays
   * active under reduced motion -- the dim is state, not motion.
   */
  const applyStaleness = useCallback((isStale: boolean) => {
    if (isStale === isStaleRef.current) return
    isStaleRef.current = isStale

    if (markerContent.current) {
      markerContent.current.style.opacity = isStale ? String(STALE_DIM_OPACITY) : ""
    } else if (markerRef.current && useRegularMarker.current && "setOpacity" in markerRef.current) {
      // Regular-marker fallback has no DOM content -- use the marker's own opacity.
      ;(markerRef.current as google.maps.Marker).setOpacity(isStale ? STALE_DIM_OPACITY : 1)
    }
    onStaleChangeRef.current?.(isStale)
  }, [])

  // PLA-272: a replacement marker starts non-stale, so report `false` on an
  // unmount mid-staleness -- the parent's flag must not outlive this marker.
  useEffect(() => {
    return () => {
      if (isStaleRef.current) {
        isStaleRef.current = false
        onStaleChangeRef.current?.(false)
      }
    }
  }, [])

  /** Paints `frameIndex` onto the marker, crossfading when asked (and allowed). */
  const applyFrame = useCallback((frameIndex: number, transition: CarSpriteTransition) => {
    if (frameIndex === renderedFrame.current) return
    renderedFrame.current = frameIndex

    // PLA-294: keep the scan mask aligned to the ACTUAL displayed sprite frame.
    // resolveFrame holds the last heading when the car is stationary (it never
    // snaps back to north), so the at-hub frame is usually NOT frame 0. If the
    // mask lagged the frame, the scan would clip to the wrong silhouette and
    // light transparent corners. Only the beam/sheen layer is masked.
    if (scanBeamRef.current) {
      const scanMaskPos = frameBackgroundPosition(frameIndex)
      scanBeamRef.current.style.maskPosition = scanMaskPos
      scanBeamRef.current.style.webkitMaskPosition = scanMaskPos
    }

    if (useRegularMarker.current) {
      if (markerRef.current && "setIcon" in markerRef.current) {
        ;(markerRef.current as google.maps.Marker).setIcon(
          fallbackIconForFrame(frameIndex, spriteVariantRef.current),
        )
      }
      return
    }

    const layers = spriteLayers.current
    if (!layers) return
    const front = layers[activeLayerIndex.current === 0 ? 0 : 1]
    const back = layers[activeLayerIndex.current === 0 ? 1 : 0]
    const position = frameBackgroundPosition(frameIndex)

    if (transition === "none" || prefersReducedMotion()) {
      // Plain frame swap (engine said none, or the user prefers reduced motion).
      front.style.transition = "none"
      back.style.transition = "none"
      front.style.backgroundPosition = position
      front.style.opacity = "1"
      back.style.opacity = "0"
      return
    }

    // Crossfade: paint the new frame on the back layer, then fade it in over
    // the front (~120ms). Opacity-only, so it stays on the compositor.
    // Reset BOTH layers to a clean 1/0 baseline first -- if a previous fade was
    // interrupted, the layers hover at intermediate opacities and restarting
    // from there blends two rotation frames (the "sheared car" artifact).
    front.style.transition = "none"
    front.style.opacity = "1"
    back.style.transition = "none"
    back.style.backgroundPosition = position
    back.style.opacity = "0"
    // Flush styles so the opacity change below transitions from the current value.
    void back.offsetWidth
    front.style.transition = `opacity ${CROSSFADE_MS}ms linear`
    back.style.transition = `opacity ${CROSSFADE_MS}ms linear`
    back.style.opacity = "1"
    front.style.opacity = "0"
    activeLayerIndex.current = activeLayerIndex.current === 0 ? 1 : 0
  }, [])

  /**
   * Adapter between interpolated marker positions and CarSpriteEngine: the
   * backend payload carries no usable heading/speed (the Pusher handler
   * hardcodes 0s), so heading is derived as the bearing between consecutive
   * interpolated positions and speed as haversine distance / elapsed time.
   * The engine's speed gate then holds the heading through stationary noise.
   * The dev harness sends real heading/speed through the same store path --
   * deriving from positions works uniformly for both.
   */
  const feedSpriteSample = useCallback(
    (lat: number, lng: number, timeMs: number) => {
      const previous = lastSampleRef.current
      lastSampleRef.current = { lat, lng, timeMs }
      if (!previous) return
      const dtMs = timeMs - previous.timeMs
      if (dtMs <= 0) return

      const meters = calculateDistance(
        { latitude: previous.lat, longitude: previous.lng },
        { latitude: lat, longitude: lng },
      )
      const speedMps = meters / (dtMs / 1000)
      const heading = calculateHeading({ lat: previous.lat, lng: previous.lng }, { lat, lng })

      const { state, output } = resolveFrame(
        spriteStateRef.current,
        { heading, speed: speedMps, timestampMs: timeMs },
        { disableCrossfade: prefersReducedMotion() },
      )
      spriteStateRef.current = state
      applyFrame(output.frameIndex, output.transition)
    },
    [applyFrame],
  )

  useEffect(() => {
    if (!map) {
      // Map not ready yet -- expose null so the update effect doesn't attempt lazy creation.
      createMarkerRef.current = null
      return
    }

    // Guard against NaN coordinates (WEB-1Q). Google Maps throws "gmp-advanced-marker:
    // lat: NaN" when the driver location arrives with missing geo data.
    // We still expose createMarker via ref so the update/animation effect can call it
    // lazily once valid coordinates arrive later (prevents the marker never appearing
    // for orders where the first Pusher payload has missing geo data).
    const createMarker = () => {
      // Never create a marker with NaN coordinates (WEB-1Q).
      if (!areCoordsValid(currentPosition.current.lat, currentPosition.current.lng)) {
        return
      }

      // Check if AdvancedMarkerElement is available
      if (!google?.maps?.marker?.AdvancedMarkerElement) {
        useRegularMarker.current = true

        // Create a regular marker showing the current sprite frame
        const marker = new google.maps.Marker({
          position: currentPosition.current,
          map,
          icon: fallbackIconForFrame(renderedFrame.current, spriteVariantRef.current),
          zIndex: 100,
          // PLA-272: a marker created while the pose is already stale starts dimmed.
          opacity: isStaleRef.current ? STALE_DIM_OPACITY : 1,
        })

        // Add click listener if onClick provided
        if (onClick) {
          marker.addListener("click", onClick)
        }

        markerRef.current = marker
        setMarkerReady(true)
        return
      }

      // Creating advanced car marker at current position

      // Create marker content if not exists (sprite DOM plumbing lives in
      // ./smooth-car-sprite). PLA-272: content created while the pose is
      // already stale starts dimmed. PLA-274: the 1x1 wrapper anchors the
      // sprite centered on the GPS position at every zoom level.
      if (!markerContent.current) {
        const { wrapper, container, layers } = buildSpriteContainer(
          renderedFrame.current,
          isStaleRef.current ? String(STALE_DIM_OPACITY) : "",
          spriteVariantRef.current,
        )
        markerWrapper.current = wrapper
        markerContent.current = container
        spriteLayers.current = layers
        activeLayerIndex.current = 0
      }

      // Create the marker
      const marker = new google.maps.marker.AdvancedMarkerElement({
        map,
        position: currentPosition.current,
        content: markerWrapper.current,
        zIndex: 100,
      })

      // Add click listener if onClick provided
      if (onClick && markerContent.current) {
        markerContent.current.style.cursor = "pointer"
        markerContent.current.addEventListener("click", onClick)
      }

      markerRef.current = marker
      setMarkerReady(true)
    }

    // Expose via ref so the update effect can call it for lazy creation when
    // coordinates transition from NaN -> valid after the initial mount.
    createMarkerRef.current = createMarker

    // Only create immediately if the incoming coordinates are already valid.
    // Seed the position refs from the valid `location` prop first -- they may still
    // hold NaN from the initial render when the first payload was NaN, and
    // createMarker() reads currentPosition.current. Without this, a NaN->valid
    // transition that completes before the map mounts would leave the marker
    // hidden because createMarker() bails on the stale NaN currentPosition.
    if (areCoordsValid(location.latitude, location.longitude)) {
      currentPosition.current = { lat: location.latitude, lng: location.longitude }
      createMarker()
    }

    return () => {
      createMarkerRef.current = null
      // Remove click listener from advanced marker content
      if (onClick && markerContent.current) {
        markerContent.current.removeEventListener("click", onClick)
      }
      if (markerRef.current) {
        if ("setMap" in markerRef.current) {
          ;(markerRef.current as google.maps.Marker).setMap(null)
        } else if ("map" in markerRef.current) {
          ;(markerRef.current as google.maps.marker.AdvancedMarkerElement).map = null
        }
        markerRef.current = null
      }
    }
  }, [map, onClick])

  // Per-frame tick: advance the motion engine and project the resulting pose
  // onto the marker + sprite engine. The engine owns segment timing, spline
  // path, and velocity profile (PLA-271, ADR-0030); reduced motion is read
  // per tick so live preference flips snap immediately. Idle-stops when the
  // engine reports rest -- every fix push below restarts the loop.
  const tick = useCallback(() => {
    const nowMs = Date.now()
    const reducedMotion = prefersReducedMotion()
    const { state, pose, isResting } = resolvePose(
      motionStateRef.current,
      { nowMs },
      { reducedMotion },
    )
    motionStateRef.current = state

    if (pose) {
      // Position/sprite writes only when the pose actually moved -- the
      // stale-watch ticks below resolve an unchanged rest pose every frame
      // and must not spam the marker or the sprite engine.
      const moved =
        pose.lat !== currentPosition.current.lat || pose.lng !== currentPosition.current.lng
      if (moved) {
        currentPosition.current = { lat: pose.lat, lng: pose.lng }
        if (markerRef.current) {
          if (useRegularMarker.current) {
            ;(markerRef.current as google.maps.Marker).setPosition({ lat: pose.lat, lng: pose.lng })
          } else {
            ;(markerRef.current as google.maps.marker.AdvancedMarkerElement).position = {
              lat: pose.lat,
              lng: pose.lng,
            }
          }
        }
        // Sprite heading/speed stay derived from consecutive displayed
        // positions -- the CarSpriteEngine contract is unchanged.
        feedSpriteSample(pose.lat, pose.lng, nowMs)
        // PLA-273: the comet trail buffers the DISPLAYED pose (never raw
        // fixes); reduced motion never buffers, and the trail module's speed
        // gate drops parked poses so the tail drains on its own.
        const trailTarget = trailRef.current
        if (trailTarget && !reducedMotion) {
          trailTarget.current = pushTrailPoint(trailTarget.current, {
            lat: pose.lat,
            lng: pose.lng,
            timestampMs: nowMs,
            speedMps: pose.speedMps,
          })
        }
      }
      applyStaleness(pose.isStale && staleAffordanceRef.current)
    }

    if (isResting) {
      // PLA-272: staleness (90s of GPS silence) can only flip while at rest --
      // exactly when this loop used to idle-stop. Keep a cheap watch tick
      // alive until the flip lands (no timers -- the engine clock stays the
      // single staleness source); once stale, idle-stop. The next pushFix
      // restarts the loop and its first tick clears the dim instantly.
      if (pose && !pose.isStale) {
        animationRef.current = requestAnimationFrame(tick)
        return
      }
      animationRef.current = null
      return
    }
    animationRef.current = requestAnimationFrame(tick)
  }, [feedSpriteSample, applyStaleness])

  const startMotionLoop = useCallback(() => {
    if (animationRef.current !== null) return
    animationRef.current = requestAnimationFrame(tick)
  }, [tick])

  // PLA-272: a placement that disables the affordance mid-staleness must
  // undim immediately -- the loop may have idle-stopped while stale, so no
  // tick would otherwise deliver the clear. Re-enabling needs no mirror
  // image: the next tick or fix re-applies the engine's true state.
  useEffect(() => {
    if (!staleAffordance) {
      applyStaleness(false)
    }
  }, [staleAffordance, applyStaleness])

  // Ingest location changes as raw fixes (replaces the legacy fixed-7000ms
  // ease-out animator and its mismatched 1000ms catch-up -- the teleport bug).
  useEffect(() => {
    // Drop updates with invalid coordinates -- never animate to NaN (WEB-1Q).
    if (!areCoordsValid(location.latitude, location.longitude)) {
      return undefined
    }

    // If the marker hasn't been created yet (first location was NaN), create it
    // now that we have valid coordinates. createMarkerRef is set by the creation
    // effect once the map is ready; if it's still null the map isn't ready yet
    // and the creation effect will handle creation when the map mounts.
    if (markerRef.current === null && createMarkerRef.current !== null) {
      // Seed the position ref with the first valid coords before creating the marker.
      currentPosition.current = { lat: location.latitude, lng: location.longitude }
      createMarkerRef.current()
      // If creation still produced null (e.g. google.maps not available), bail out.
      if (markerRef.current === null) {
        return undefined
      }
    }

    const nowMs = Date.now()
    motionStateRef.current = pushFix(
      motionStateRef.current,
      {
        lat: location.latitude,
        lng: location.longitude,
        // Payload timestamps can be missing or unreliable (server clock);
        // the engine falls back to arrival-clock gaps when they are.
        timestampMs: location.timestamp ?? nowMs,
      },
      nowMs,
      { reducedMotion: prefersReducedMotion() },
    )
    startMotionLoop()

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
    }
    // location.timestamp is deliberately a dependency: a parked/slow driver
    // legitimately re-sends IDENTICAL coordinates with fresh timestamps, and
    // each such fix must refresh the engine's staleness baseline or the car
    // dims after 90s despite a live signal. Static placements must therefore
    // pass a STABLE timestamp (or none) -- a per-render Date.now() would
    // re-ingest on every parent render.
  }, [location.latitude, location.longitude, location.timestamp, startMotionLoop])

  // Return null as we're managing the marker imperatively
  return null
}
